/**
 * Keyboard input handler for RPN Calculator
 * Handles touch, mouse, and physical keyboard input
 */

import type { KeyStroke } from '../calculator/types';
import { PHYSICAL_KEY_MAP } from '../calculator/types';

export type KeyboardCallback = (key: KeyStroke) => void;
export type ButtonStateCallback = (element: HTMLElement, pressed: boolean) => void;

export class Keyboard {
  private keyboardElement: HTMLElement;
  private onKeyPress: KeyboardCallback;
  private onButtonState?: ButtonStateCallback;
  private audioEnabled = true;

  // Web Audio API for zero-allocation click sound
  private audioCtx: AudioContext | null = null;
  private clickBuffer: AudioBuffer | null = null;

  // Track the currently pressed button to avoid querySelectorAll
  private activeButton: HTMLElement | null = null;

  constructor(
    keyboardElement: HTMLElement,
    onKeyPress: KeyboardCallback,
    options?: {
      onButtonState?: ButtonStateCallback;
      audioEnabled?: boolean;
    }
  ) {
    this.keyboardElement = keyboardElement;
    this.onKeyPress = onKeyPress;
    this.onButtonState = options?.onButtonState;
    this.audioEnabled = options?.audioEnabled ?? true;

    this.initAudio();
    this.bindTouchEvents();
    this.bindMouseEvents();
    this.bindKeyboardEvents();
  }

  /**
   * Initialize Web Audio API with pre-decoded click buffer
   */
  private initAudio(): void {
    try {
      this.audioCtx = new AudioContext();
      fetch('/sound/click.mp3')
        .then(res => res.arrayBuffer())
        .then(buf => this.audioCtx!.decodeAudioData(buf))
        .then(decoded => { this.clickBuffer = decoded; })
        .catch(() => {});
    } catch {
      // Web Audio not supported
    }
  }

  /**
   * Play click sound (zero allocation per call)
   */
  private playClick(): void {
    if (!this.audioEnabled || !this.audioCtx || !this.clickBuffer) return;

    // Resume context if suspended (autoplay policy)
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    const source = this.audioCtx.createBufferSource();
    const gain = this.audioCtx.createGain();
    source.buffer = this.clickBuffer;
    gain.gain.value = 0.5;
    source.connect(gain).connect(this.audioCtx.destination);
    source.start();
  }

  /**
   * Set audio feedback enabled/disabled
   */
  setAudioEnabled(enabled: boolean): void {
    this.audioEnabled = enabled;
  }

  /**
   * Find the button ID from a touch/click target
   */
  private getButtonId(target: EventTarget | null): KeyStroke | null {
    if (!(target instanceof HTMLElement)) return null;

    // Check if it's a taparea element
    if (target.classList.contains('taparea') && target.id) {
      return target.id as KeyStroke;
    }

    // Check parent for button
    const button = target.closest('.button');
    if (button) {
      const taparea = button.querySelector('.taparea');
      if (taparea?.id) {
        return taparea.id as KeyStroke;
      }
    }

    return null;
  }

  /**
   * Get the button element containing a taparea
   */
  private getButtonElement(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof HTMLElement)) return null;

    if (target.classList.contains('button')) {
      return target;
    }

    return target.closest('.button') as HTMLElement | null;
  }

  /**
   * Release the currently active button
   */
  private releaseActiveButton(): void {
    if (this.activeButton) {
      this.activeButton.classList.remove('down');
      this.onButtonState?.(this.activeButton, false);
      this.activeButton = null;
    }
  }

  /**
   * Bind touch events for mobile/tablet
   */
  private bindTouchEvents(): void {
    this.keyboardElement.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      const target = e.target;
      const buttonId = this.getButtonId(target);
      const buttonEl = this.getButtonElement(target);

      if (buttonId && buttonEl) {
        this.releaseActiveButton();
        this.activeButton = buttonEl;
        buttonEl.classList.add('down');
        this.playClick();
        this.onButtonState?.(buttonEl, true);
      }
    }, { passive: false });

    this.keyboardElement.addEventListener('touchend', (e: TouchEvent) => {
      e.preventDefault();
      const target = e.target;
      const buttonId = this.getButtonId(target);

      if (buttonId) {
        this.onKeyPress(buttonId);
      }

      this.releaseActiveButton();
    }, { passive: false });

    this.keyboardElement.addEventListener('touchcancel', () => {
      this.releaseActiveButton();
    });
  }

  /**
   * Bind mouse events for desktop
   */
  private bindMouseEvents(): void {
    this.keyboardElement.addEventListener('mousedown', (e: MouseEvent) => {
      const buttonId = this.getButtonId(e.target);
      const buttonEl = this.getButtonElement(e.target);

      if (buttonId && buttonEl) {
        this.releaseActiveButton();
        this.activeButton = buttonEl;
        buttonEl.classList.add('down');
        this.playClick();
        this.onButtonState?.(buttonEl, true);
      }
    });

    this.keyboardElement.addEventListener('mouseup', (e: MouseEvent) => {
      const buttonId = this.getButtonId(e.target);

      if (buttonId) {
        this.onKeyPress(buttonId);
      }

      this.releaseActiveButton();
    });

    this.keyboardElement.addEventListener('mouseleave', () => {
      this.releaseActiveButton();
    });
  }

  /**
   * Bind physical keyboard events
   */
  private bindKeyboardEvents(): void {
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      // Don't capture if typing in input fields
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = e.key;
      const mappedKey = PHYSICAL_KEY_MAP[key];

      if (mappedKey) {
        e.preventDefault();

        // Find and highlight the corresponding button
        const taparea = document.getElementById(mappedKey);
        const buttonEl = taparea?.closest('.button') as HTMLElement | null;

        if (buttonEl) {
          this.releaseActiveButton();
          this.activeButton = buttonEl;
          buttonEl.classList.add('down');
          this.playClick();
          this.onButtonState?.(buttonEl, true);
        }

        this.onKeyPress(mappedKey);

        // Remove highlight after short delay
        setTimeout(() => {
          this.releaseActiveButton();
        }, 100);
      }
    });
  }

  /**
   * Programmatically trigger a key press
   */
  triggerKey(key: KeyStroke): void {
    this.onKeyPress(key);
  }

  /**
   * Destroy event listeners
   */
  destroy(): void {
    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }
  }
}
